import { useEffect, useMemo, useRef, useState } from 'react';
import { nuevaClaveIntento } from '@renderer/lib/claveIntento';
import {
  MapPin,
  Minus,
  Plus,
  ShoppingBasket,
  ArrowLeft,
  Check,
  Loader2,
  StickyNote,
} from 'lucide-react';
import { NotaProductoModal } from '@renderer/components/pos/NotaProducto';
import type { GrupoNotasRapidas } from '@shared/notasRapidas';
import { MESES_CONSERVAR_DATOS_CLIENTE } from '@shared/privacidad';
import { AvisoPrivacidadHoja } from '@renderer/components/publico/AvisoPrivacidad';

/**
 * Sprint 92 — El menú que el cliente abre desde WhatsApp.
 *
 * El 90% de las ventas nace en un chat, 50 a 150 al día, y hoy cada una la teclea una persona. Esta
 * pantalla es la que puede cambiar eso: el cliente arma su pedido y llega al tablero listo.
 *
 * ## Cuatro pasos, un pulgar, sin registro
 *
 * La regla de diseño es una: **nada que el cliente pueda no contestar**. No hay contraseña, no hay
 * cuenta, no hay correo. Se pregunta lo que sólo él sabe —qué quiere, a dónde va, cómo se llama— y
 * nada más.
 *
 * ## El detalle que viene del Sprint 91
 *
 * Los tamaños de un producto van en **una sola tarjeta**, no en dos. El catálogo guarda "Frijoles
 * Charros 1 litro" y "1/2 litro" como productos distintos porque así se descuenta el inventario;
 * enseñárselo así al cliente sería mostrarle nuestro modelo de datos. Es exactamente el problema
 * que el dueño del producto detectó del lado del operador, y que aquí se multiplicaría por 150
 * personas al día.
 *
 * ## Lo que esta pantalla NO puede prometer
 *
 * **El costo del envío.** Lo pone el grupo de motomandados por WhatsApp, así que antes de cotizar
 * sólo se puede estimar por zona. Se dice "aprox." y se explica; inventar un total exacto sería
 * mentirle al cliente en la única cifra que le importa.
 */

interface ProductoPublico {
  id: number;
  nombre: string;
  categoria: string | null;
  precio: number;
  disponible: boolean;
  tamano?: string;
  grupo?: string;
}

interface Menu {
  negocio: { nombre: string; monedaSimbolo: string; logoUrl: string | null };
  compraMinima: number;
  categorias: string[];
  productos: ProductoPublico[];
  zonas: Array<{ nombre: string; tarifa: number }>;
  /**
   * Sprint 137 — ¿El negocio deja elegir "pago al recibir"? Apagado de fábrica.
   *
   * Opcional para que una respuesta vieja del servidor no rompa la página: sin el campo, la
   * opción simplemente no se pinta.
   */
  contraEntregaDisponible?: boolean;
  /**
   * Sprint 138 — Los botones de "cómo lo quieres" que configuró el negocio.
   *
   * Opcional por la misma razón que el de arriba: si el servidor todavía no los manda, la página
   * enseña el campo de texto libre y nadie se queda sin poder pedir su término.
   */
  notasRapidas?: GrupoNotasRapidas[];
}

type Paso = 'menu' | 'canasta' | 'entrega' | 'listo';

const money = (centavos: number, simbolo = '$') =>
  `${simbolo}${(centavos / 100).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function MenuPublicoPage() {
  const [menu, setMenu] = useState<Menu | null>(null);
  const [cargando, setCargando] = useState(true);
  const [noDisponible, setNoDisponible] = useState(false);
  const [paso, setPaso] = useState<Paso>('menu');
  const [categoria, setCategoria] = useState<string | null>(null);
  const [carrito, setCarrito] = useState<Record<number, number>>({});
  /** Sprint 138 — Cómo quiere el cliente cada producto. Clave = productoId. */
  const [notas, setNotas] = useState<Record<number, string>>({});
  const [anotando, setAnotando] = useState<number | null>(null);

  /**
   * Sprint 105 — El cliente no vuelve a teclear lo que ya escribió.
   *
   * La dueña preguntó si se podían reconocer sus clientes frecuentes **por la ubicación GPS**. Por
   * ahí no: dos vecinos del mismo edificio dan el mismo punto y la precisión varía entre cinco y
   * cincuenta metros, así que acabaría enseñando la dirección de un cliente en el pedido de otro.
   * El GPS ubica una casa, no identifica a una persona.
   *
   * Esto da lo mismo que ella quería —que el cliente frecuente no vuelva a capturar nada— sin
   * tocar datos de terceros: **cada quien lleva sus datos en su propio teléfono**. No hay perfil
   * que cruzar, no hay nada que adivinar, y si presta el celular o borra su navegador, sólo se
   * pierde su propia comodidad.
   *
   * El negocio sigue reconociendo al cliente recurrente **por su teléfono**, que es la llave
   * correcta y ya existe desde el Sprint 35.
   */
  const [nombre, setNombre] = useState(() => leerRecordado('nombre'));
  const [telefono, setTelefono] = useState(() => leerRecordado('telefono'));
  const [direccion, setDireccion] = useState(() => leerRecordado('direccion'));
  const [referencias, setReferencias] = useState(() => leerRecordado('referencias'));
  /** Sprint 167 — El aviso de privacidad completo, encima del menú (salir de él perdería la canasta). */
  const [verAviso, setVerAviso] = useState(false);
  const [hayRecordado, setHayRecordado] = useState(() => Boolean(leerRecordado('telefono') || leerRecordado('nombre')));
  const [gps, setGps] = useState<{ lat: number; lng: number } | null>(null);
  /**
   * Sprint 137 — Cómo quiere pagar el cliente.
   *
   * Arranca en transferencia, que es como opera el negocio. Lo que se manda es una **petición**:
   * el servidor sólo la respeta si el negocio tiene la modalidad encendida, y en cualquier caso
   * el pedido sigue pasando por la revisión de una persona antes de tocar la cocina.
   */
  const [pagaAlRecibir, setPagaAlRecibir] = useState(false);
  const [ubicando, setUbicando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  /*
    Sprint 162 (DT-167) — Un toque, un pedido, también para el cliente. Con mala señal el cliente
    toca "Mandar" dos veces o el navegador reintenta: sin esto, la dueña recibía dos pedidos iguales
    en "Llegaron del enlace". El ref frena en el acto; la clave hace que el servidor reconozca el
    reintento y conteste con el pedido que ya se creó.
  */
  const enviandoRef = useRef(false);
  const intentoRef = useRef<{ firma: string; clave: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [folio, setFolio] = useState<number | null>(null);
  /** Sprint 96 — La llave con la que vuelve a ver su pedido. */
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const r = await fetch('/api/publico/menu');
        if (r.status === 404) {
          setNoDisponible(true);
          return;
        }
        const d = await r.json();
        if (d.ok) setMenu(d.menu);
        else setNoDisponible(true);
      } catch {
        setNoDisponible(true);
      } finally {
        setCargando(false);
      }
    })();
  }, []);

  /**
   * Los productos, agrupados como el cliente los entiende: una tarjeta con sus tamaños.
   *
   * Sin esto, quien vende frijoles en dos medidas le mostraría al cliente dos tarjetas idénticas
   * salvo por un sufijo. Es nuestro modelo de datos filtrándose a la pantalla de 150 personas.
   */
  const tarjetas = useMemo(() => {
    if (!menu) return [];
    const porGrupo = new Map<string, ProductoPublico[]>();
    const sueltos: ProductoPublico[] = [];
    for (const p of menu.productos) {
      if (categoria && p.categoria !== categoria) continue;
      if (p.grupo && p.tamano) {
        const lista = porGrupo.get(p.grupo) ?? [];
        lista.push(p);
        porGrupo.set(p.grupo, lista);
      } else {
        sueltos.push(p);
      }
    }
    const agrupados = [...porGrupo.entries()].map(([nombreGrupo, items]) => ({
      titulo: nombreGrupo,
      // De mayor a menor, como en la carta.
      opciones: items.sort((a, b) => b.precio - a.precio),
    }));
    const solos = sueltos.map((p) => ({ titulo: p.nombre, opciones: [p] }));
    return [...agrupados, ...solos].sort((a, b) => a.titulo.localeCompare(b.titulo, 'es'));
  }, [menu, categoria]);

  const porId = useMemo(() => new Map((menu?.productos ?? []).map((p) => [p.id, p])), [menu]);
  const lineas = Object.entries(carrito)
    .map(([id, cant]) => ({ p: porId.get(Number(id))!, cant }))
    .filter((l) => l.p && l.cant > 0);
  const total = lineas.reduce((s, l) => s + l.p.precio * l.cant, 0);
  const piezas = lineas.reduce((s, l) => s + l.cant, 0);
  const simbolo = menu?.negocio.monedaSimbolo ?? '$';
  const faltaParaMinimo = menu && menu.compraMinima > 0 ? menu.compraMinima - total : 0;

  const sumar = (id: number, delta: number) =>
    setCarrito((c) => {
      const n = Math.max(0, (c[id] ?? 0) + delta);
      const copia = { ...c };
      if (n === 0) {
        delete copia[id];
        // Sprint 138 — Si el producto ya no va, su nota tampoco: quedaría colgada y se mandaría
        // sola si el cliente lo vuelve a agregar más tarde por otra razón.
        setNotas((prev) => {
          if (!(id in prev)) return prev;
          const sinNota = { ...prev };
          delete sinNota[id];
          return sinNota;
        });
      } else copia[id] = n;
      return copia;
    });

  /** Un toque para el GPS: es la misma ubicación que ya manda por WhatsApp. */
  const ubicarme = () => {
    if (!navigator.geolocation) {
      setError('Tu teléfono no permite compartir ubicación. Escribe tu dirección abajo.');
      return;
    }
    setUbicando(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setUbicando(false);
      },
      () => {
        setUbicando(false);
        setError('No pudimos leer tu ubicación. Escribe tu dirección abajo y listo.');
      },
      { enableHighAccuracy: true, timeout: 12000 },
    );
  };

  const enviar = async () => {
    if (enviandoRef.current) return;
    enviandoRef.current = true;
    setEnviando(true);
    setError(null);
    try {
      const cuerpo = {
          // Sólo ids, cantidades y cómo lo quiere: el precio lo pone el servidor.
          items: lineas.map((l) => ({
            productoId: l.p.id,
            cantidad: l.cant,
            nota: notas[l.p.id] || undefined,
          })),
          clienteNombre: nombre.trim(),
          clienteTelefono: telefono.trim(),
          clienteDireccion: direccion.trim() || undefined,
          clienteLatitud: gps?.lat,
          clienteLongitud: gps?.lng,
          referencias: referencias.trim() || undefined,
          /*
            Sprint 137 — Cómo quiere pagar. Es una petición: la valida el servidor contra el
            ajuste del negocio, y aunque la acepte el pedido sigue pasando por revisión humana.
          */
          pagaAlEntregar: pagaAlRecibir || undefined,
      };
      const firma = JSON.stringify(cuerpo);
      if (intentoRef.current?.firma !== firma) {
        const clave = nuevaClaveIntento();
        intentoRef.current = clave ? { firma, clave } : null;
      }
      const r = await fetch('/api/publico/pedido', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...cuerpo, claveIntento: intentoRef.current?.clave }),
      });
      const d = await r.json();
      if (!d.ok) {
        setError(d.error ?? 'No pudimos recibir tu pedido.');
        setEnviando(false);
        enviandoRef.current = false;
        return;
      }
      intentoRef.current = null;
      // Se recuerda sólo cuando el pedido salió bien: guardar datos de un intento fallido sería
      // recordar algo que quizá el propio cliente estaba corrigiendo.
      recordar({
        nombre: nombre.trim(),
        telefono: telefono.trim(),
        direccion: direccion.trim(),
        referencias: referencias.trim(),
      });
      setFolio(d.numeroPedido);
      setToken(d.token ?? null);
      setPaso('listo');
    } catch {
      setError('No pudimos recibir tu pedido. Márcanos por WhatsApp.');
    }
    setEnviando(false);
    enviandoRef.current = false;
  };

  if (cargando) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (noDisponible || !menu) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 p-6 text-center">
        <div>
          <div className="text-5xl">🍗</div>
          <h1 className="mt-3 text-xl font-bold">El menú en línea no está disponible</h1>
          <p className="mt-1 text-sm text-slate-600">
            Márcanos por WhatsApp y con gusto tomamos tu pedido.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col bg-white">
      {/* ───────── Paso 1: el menú ───────── */}
      {paso === 'menu' && (
        <>
          <header className="bg-primary px-4 pb-4 pt-5 text-white">
            <div className="text-lg font-extrabold">{menu.negocio.nombre}</div>
            <div className="text-xs opacity-90">Arma tu pedido y te lo llevamos</div>
          </header>

          {menu.categorias.length > 0 && (
            <div className="sticky top-0 z-10 flex gap-2 overflow-auto border-b border-slate-200 bg-white px-3 py-3">
              <Chip activo={categoria === null} onClick={() => setCategoria(null)}>
                Todo
              </Chip>
              {menu.categorias.map((c) => (
                <Chip key={c} activo={categoria === c} onClick={() => setCategoria(c)}>
                  {c}
                </Chip>
              ))}
            </div>
          )}

          <div className="flex-1 pb-28">
            {tarjetas.map((t) => (
              <div key={t.titulo} className="border-b border-slate-100 px-4 py-3">
                <div className="font-bold">{t.titulo}</div>
                <div className="mt-2 space-y-2">
                  {t.opciones.map((o) => (
                    <div key={o.id} className="flex items-center gap-3">
                      <div className={`min-w-0 flex-1 ${o.disponible ? '' : 'opacity-50'}`}>
                        <div className="text-sm">
                          {o.tamano ?? o.nombre}
                          {!o.disponible && (
                            <span className="ml-2 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700">
                              se acabó hoy
                            </span>
                          )}
                        </div>
                        <div className="font-mono text-sm font-bold">{money(o.precio, simbolo)}</div>
                      </div>
                      {o.disponible &&
                        ((carrito[o.id] ?? 0) > 0 ? (
                          <div className="flex shrink-0 items-center gap-2">
                            <button
                              onClick={() => sumar(o.id, -1)}
                              className="h-11 w-11 rounded-xl border-2 border-primary text-lg font-bold text-primary"
                              aria-label="Quitar uno"
                            >
                              <Minus className="mx-auto h-4 w-4" />
                            </button>
                            <span className="w-6 text-center font-extrabold">{carrito[o.id]}</span>
                            <button
                              onClick={() => sumar(o.id, 1)}
                              className="h-11 w-11 rounded-xl border-2 border-primary text-lg font-bold text-primary"
                              aria-label="Agregar uno"
                            >
                              <Plus className="mx-auto h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => sumar(o.id, 1)}
                            className="h-11 w-11 shrink-0 rounded-xl bg-primary text-white"
                            aria-label={`Agregar ${o.nombre}`}
                          >
                            <Plus className="mx-auto h-5 w-5" />
                          </button>
                        ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {piezas > 0 && (
            <div className="fixed inset-x-0 bottom-0 mx-auto max-w-md border-t-2 border-slate-200 bg-white p-3">
              <button
                onClick={() => setPaso('canasta')}
                className="flex min-h-[52px] w-full items-center justify-between rounded-xl bg-primary px-4 font-extrabold text-white"
              >
                <span className="flex items-center gap-2">
                  <ShoppingBasket className="h-5 w-5" />
                  {piezas} producto{piezas === 1 ? '' : 's'}
                </span>
                <span>{money(total, simbolo)} →</span>
              </button>
            </div>
          )}
        </>
      )}

      {/* ───────── Paso 2: la canasta ───────── */}
      {paso === 'canasta' && (
        <div className="flex-1 px-4 py-5">
          <Volver onClick={() => setPaso('menu')} />
          <h1 className="mt-2 text-xl font-extrabold">Tu pedido</h1>
          <p className="text-xs text-slate-500">Revísalo antes de mandarlo</p>

          <ul className="mt-4">
            {lineas.map((l) => (
              <li
                key={l.p.id}
                className="border-b border-dashed border-slate-200 py-3 text-sm"
              >
                <div className="flex justify-between gap-3">
                  <span>
                    <b className="text-primary">{l.cant}</b> {l.p.nombre}
                  </span>
                  <span className="font-mono">{money(l.p.precio * l.cant, simbolo)}</span>
                </div>
                {/*
                  Sprint 138 — El cliente dice cómo lo quiere, con sus propias palabras o con los
                  botones de la casa. Va en la canasta y no en el menú: aquí ya decidió qué lleva
                  y está revisando, que es cuando uno se acuerda de "ah, y bien cocida".
                */}
                <button
                  type="button"
                  onClick={() => setAnotando(l.p.id)}
                  className={`mt-2 flex min-h-[44px] w-full items-center gap-2 rounded-lg border px-3 text-left ${
                    notas[l.p.id]
                      ? 'border-orange-300 bg-orange-50 font-semibold text-orange-900'
                      : 'border-dashed border-slate-300 text-slate-600'
                  }`}
                >
                  <StickyNote className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate">
                    {notas[l.p.id] || '¿Cómo lo quieres? (opcional)'}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex justify-between text-lg font-extrabold">
            <span>Comida</span>
            <span className="font-mono">{money(total, simbolo)}</span>
          </div>

          {/*
            El envío. No se puede prometer el exacto: lo pone el grupo de motomandados. Se dice
            "aprox." y se explica — inventar el total sería mentir en la cifra que más importa.
          */}
          {menu.zonas.length > 0 && (
            <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3">
              <div className="text-sm font-bold text-amber-900">Falta el envío</div>
              <div className="mt-1 text-xs text-amber-800">
                Por tu rumbo suele costar entre{' '}
                <b>{money(Math.min(...menu.zonas.map((z) => z.tarifa)), simbolo)}</b> y{' '}
                <b>{money(Math.max(...menu.zonas.map((z) => z.tarifa)), simbolo)}</b>. Te
                confirmamos el precio exacto por WhatsApp en cuanto lo cotizamos con el repartidor.
              </div>
            </div>
          )}

          {faltaParaMinimo > 0 && (
            <div className="mt-4 rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
              Te faltan <b>{money(faltaParaMinimo, simbolo)}</b> para el mínimo de{' '}
              <b>{money(menu.compraMinima, simbolo)}</b> a domicilio.
            </div>
          )}

          <button
            onClick={() => setPaso('entrega')}
            disabled={faltaParaMinimo > 0}
            className="mt-5 min-h-[52px] w-full rounded-xl bg-primary font-extrabold text-white disabled:opacity-40"
          >
            Continuar →
          </button>
          <button
            onClick={() => setPaso('menu')}
            className="mt-2 min-h-[48px] w-full rounded-xl border border-slate-300 font-semibold"
          >
            Seguir agregando
          </button>
        </div>
      )}

      {/* ───────── Paso 3: a dónde va ───────── */}
      {paso === 'entrega' && (
        <div className="flex-1 px-4 py-5">
          <Volver onClick={() => setPaso('canasta')} />
          <h1 className="mt-2 text-xl font-extrabold">¿A dónde te lo llevamos?</h1>
          <p className="text-xs text-slate-500">Dos datos y ya.</p>

          <button
            onClick={ubicarme}
            disabled={ubicando}
            className={`mt-4 min-h-[64px] w-full rounded-xl font-bold text-white ${
              gps ? 'bg-emerald-600' : 'bg-emerald-600'
            }`}
          >
            {ubicando ? (
              <Loader2 className="mx-auto h-5 w-5 animate-spin" />
            ) : gps ? (
              <span className="flex items-center justify-center gap-2">
                <Check className="h-5 w-5" /> Ubicación compartida
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <MapPin className="h-5 w-5" /> Compartir mi ubicación
              </span>
            )}
          </button>
          <p className="mt-1 text-center text-xs text-slate-500">
            Un toque. Es la misma ubicación que mandas por WhatsApp.
          </p>

          <Campo etiqueta="¿A nombre de quién?" valor={nombre} onChange={setNombre} max={60} />
          <Campo
            etiqueta="Tu WhatsApp"
            valor={telefono}
            onChange={setTelefono}
            max={20}
            tipo="tel"
            ayuda="Para avisarte cuando salga tu pedido."
          />
          <Campo
            etiqueta={gps ? 'Calle y número (opcional)' : 'Tu dirección'}
            valor={direccion}
            onChange={setDireccion}
            max={300}
          />
          <Campo
            etiqueta="Alguna referencia (opcional)"
            valor={referencias}
            onChange={setReferencias}
            max={200}
            placeholder="Portón negro, casa de dos pisos…"
          />

          {/*
            Sprint 137 — Cómo quiere pagar, sólo si el negocio lo ofrece.

            Se pregunta aquí y no antes porque es lo último que se decide en una conversación
            real. Y se dice con las dos palabras que el cliente entiende —"transferencia" o "al
            recibir"—, sin hablar de estados ni de cocina: lo que pase después es problema del
            negocio, no suyo.
          */}
          {menu.contraEntregaDisponible && (
            <fieldset className="mt-4">
              <legend className="mb-2 text-sm font-semibold text-slate-700">¿Cómo pagas?</legend>
              <label className="mb-2 flex min-h-[56px] cursor-pointer items-center gap-3 rounded-xl border-2 border-slate-200 px-3 py-2 has-[:checked]:border-primary has-[:checked]:bg-blue-50">
                <input
                  type="radio"
                  name="comoPaga"
                  checked={!pagaAlRecibir}
                  onChange={() => setPagaAlRecibir(false)}
                  className="h-5 w-5"
                />
                <span className="text-sm">
                  <span className="block font-semibold">Transferencia</span>
                  <span className="block text-xs text-slate-600">
                    Te pasamos los datos por WhatsApp.
                  </span>
                </span>
              </label>
              <label className="flex min-h-[56px] cursor-pointer items-center gap-3 rounded-xl border-2 border-slate-200 px-3 py-2 has-[:checked]:border-primary has-[:checked]:bg-blue-50">
                <input
                  type="radio"
                  name="comoPaga"
                  checked={pagaAlRecibir}
                  onChange={() => setPagaAlRecibir(true)}
                  className="h-5 w-5"
                />
                <span className="text-sm">
                  <span className="block font-semibold">Pago al recibir</span>
                  <span className="block text-xs text-slate-600">
                    Le pagas a quien te lo lleve. Te confirmamos antes de prepararlo.
                  </span>
                </span>
              </label>
            </fieldset>
          )}

          {/*
            Sprint 167 — El aviso de privacidad corto, donde el cliente acaba de escribir sus datos y
            antes del botón que los manda (Ley de Protección de Datos Personales, 2025): quién los
            usa, para qué, cuánto tiempo, y el aviso completo a un toque. Sin casilla que marcar: los
            datos no son sensibles y pedir una palomita más es un paso más para comprar pollo.
          */}
          <div className="mt-5 rounded-xl bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">
            <b>{menu.negocio.nombre}</b> usa tu nombre, tu WhatsApp y tu dirección sólo para preparar, cobrar y
            entregarte tu pedido. No los vendemos ni los compartimos para publicidad de nadie más. Se borran
            solos si pasan {MESES_CONSERVAR_DATOS_CLIENTE} meses sin que pidas.
            <button
              type="button"
              onClick={() => setVerAviso(true)}
              className="mt-1 flex min-h-[44px] items-center font-semibold text-primary underline"
            >
              Leer el aviso de privacidad
            </button>
            {hayRecordado && (
              <button
                type="button"
                onClick={() => {
                  olvidarRecordado();
                  setNombre('');
                  setTelefono('');
                  setDireccion('');
                  setReferencias('');
                  setHayRecordado(false);
                }}
                className="flex min-h-[44px] items-center font-semibold text-slate-700 underline"
              >
                Borrar mis datos de este teléfono
              </button>
            )}
          </div>
          <AvisoPrivacidadHoja abierto={verAviso} onCerrar={() => setVerAviso(false)} />

          {error && (
            <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
              {error}
            </div>
          )}

          <button
            onClick={() => void enviar()}
            disabled={enviando || nombre.trim().length < 2 || telefono.replace(/\D/g, '').length < 10}
            className="mt-5 min-h-[56px] w-full rounded-xl bg-primary text-lg font-extrabold text-white disabled:opacity-40"
          >
            {enviando ? 'Mandando…' : 'Mandar mi pedido →'}
          </button>
        </div>
      )}

      {/* ───────── Paso 4: listo ───────── */}
      {paso === 'listo' && (
        <div className="flex-1 px-4 py-8 text-center">
          <div className="text-5xl">✅</div>
          <h1 className="mt-3 text-xl font-extrabold">¡Listo, {nombre.split(' ')[0]}!</h1>
          <p className="mt-1 text-sm text-slate-600">
            Tu pedido es el <b className="font-mono">#{String(folio ?? 0).padStart(5, '0')}</b>
          </p>

          {/*
            Sprint 96 — El pedido ya no muere aquí.

            Antes esta pantalla decía "ya puedes cerrar esta página" y el cliente se quedaba sin
            saber cuánto iba a pagar ni a dónde depositar. Ahora se lleva su enlace: cuando el
            negocio cotice el envío, ahí mismo aparecen su total exacto y los datos del banco.
          */}
          <div className="mt-6 rounded-xl bg-slate-50 p-4 text-left text-sm">
            <div className="font-bold">¿Qué sigue?</div>
            <ol className="mt-2 space-y-1 text-slate-700">
              <li>1 · Cotizamos tu envío (unos minutos).</li>
              <li>2 · Aquí mismo aparece tu total y a dónde depositar.</li>
              <li>3 · En cuanto entra tu pago, se va a la parrilla.</li>
            </ol>
          </div>

          {token && (
            <a
              href={`#/pedido/${token}`}
              className="mt-5 flex min-h-[56px] w-full items-center justify-center rounded-xl bg-primary text-lg font-extrabold text-white"
            >
              Ver cómo va mi pedido →
            </a>
          )}
          <p className="mt-3 text-xs text-slate-500">
            <b>No cierres esta página</b> todavía, o guarda el enlace: es donde vas a ver tu total.
          </p>
        </div>
      )}

      {/*
        Sprint 138 — Los mismos botones que usa el mostrador, con las palabras del negocio. Llegan
        dentro de la respuesta del menú: esta página no tiene sesión para consultarlos.
      */}
      <NotaProductoModal
        abierto={anotando !== null}
        nombreProducto={porId.get(anotando ?? -1)?.nombre ?? ''}
        notaActual={anotando !== null ? notas[anotando] ?? null : null}
        grupos={menu?.notasRapidas ?? []}
        /* Aquí quien lee es el cliente: la pregunta se le hace a él, no sobre él. */
        tuteaAlCliente
        /*
          Sprint 139 — Si lleva varios del mismo producto, la nota vale para todos y hay que
          decirlo. En el mostrador anotar separa el renglón; aquí el carrito no tiene renglones,
          y callarlo haría que alguien pidiera "término medio" creyendo que es para una sola.
        */
        aviso={
          anotando !== null && (carrito[anotando] ?? 0) > 1
            ? `Llevas ${carrito[anotando]}. Lo que escribas aquí vale para todos; si los quieres distintos, dilo en el texto.`
            : null
        }
        onGuardar={(nota) => {
          if (anotando === null) return;
          setNotas((prev) => {
            const copia = { ...prev };
            if (nota.trim() === '') delete copia[anotando];
            else copia[anotando] = nota;
            return copia;
          });
        }}
        onCerrar={() => setAnotando(null)}
      />
    </div>
  );
}

function Chip({
  activo,
  onClick,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`min-h-[40px] whitespace-nowrap rounded-full border px-4 text-sm font-semibold ${
        activo ? 'border-primary bg-primary text-white' : 'border-slate-300 bg-white text-slate-700'
      }`}
    >
      {children}
    </button>
  );
}

function Volver({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex min-h-[44px] items-center gap-1 text-sm text-slate-600">
      <ArrowLeft className="h-4 w-4" />
      Atrás
    </button>
  );
}

function Campo({
  etiqueta,
  valor,
  onChange,
  max,
  tipo,
  ayuda,
  placeholder,
}: {
  etiqueta: string;
  valor: string;
  onChange: (v: string) => void;
  max: number;
  tipo?: string;
  ayuda?: string;
  placeholder?: string;
}) {
  return (
    <div className="mt-4">
      <label className="text-sm font-bold text-slate-700">{etiqueta}</label>
      <input
        className="mt-1 min-h-[48px] w-full rounded-xl border border-slate-300 px-3 text-base"
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        maxLength={max}
        type={tipo}
        inputMode={tipo === 'tel' ? 'tel' : undefined}
        placeholder={placeholder}
      />
      {ayuda && <p className="mt-1 text-xs text-slate-500">{ayuda}</p>}
    </div>
  );
}

/**
 * Sprint 105 — Lo que el cliente ya escribió, guardado en SU teléfono.
 *
 * `localStorage` y no una cookie: no viaja al servidor en cada petición, no se comparte entre
 * dispositivos y no hay nada que el negocio pueda cruzar. Es comodidad del cliente, no un perfil.
 *
 * Todo va dentro de un `try`: en navegación privada `localStorage` lanza excepción, y quedarse sin
 * autocompletar no puede impedir que alguien haga un pedido.
 */
const LLAVE_RECORDADO = 'menu-publico:cliente';

function leerRecordado(campo: 'nombre' | 'telefono' | 'direccion' | 'referencias'): string {
  try {
    const crudo = window.localStorage.getItem(LLAVE_RECORDADO);
    if (!crudo) return '';
    const d = JSON.parse(crudo) as Record<string, unknown>;
    const v = d[campo];
    return typeof v === 'string' ? v.slice(0, 200) : '';
  } catch {
    return '';
  }
}

/** Sprint 167 — "Borrar mis datos de este teléfono": lo que se recordó aquí, fuera. */
function olvidarRecordado(): void {
  try {
    window.localStorage.removeItem(LLAVE_RECORDADO);
  } catch {
    /* navegación privada: no había nada guardado */
  }
}

function recordar(datos: {
  nombre: string;
  telefono: string;
  direccion: string;
  referencias: string;
}): void {
  try {
    window.localStorage.setItem(LLAVE_RECORDADO, JSON.stringify(datos));
  } catch {
    /* navegación privada o almacenamiento lleno: no pasa nada, sólo no se recuerda */
  }
}
